import { Badge, Loader, Text } from '@mantine/core'
import {
  formatDateTime,
  formatRelative,
  runDuration,
  RUN_STATUS_COLORS,
  type RunSelection,
  type TaskRunInfo,
} from './hooks'

interface RunListProps {
  runs: TaskRunInfo[]
  loading: boolean
  showSlug: boolean
  selectedRunId: number | null
  onSelect: (selection: RunSelection) => void
}

/** Bottom-left panel: the selected task's runs (or all runs), newest first. */
export function RunList({ runs, loading, showSlug, selectedRunId, onSelect }: RunListProps) {
  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader size="sm" />
      </div>
    )
  }
  if (runs.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <Text c="dimmed" size="sm">
          No runs yet
        </Text>
      </div>
    )
  }
  return (
    <div>
      {runs.map((run) => (
        <button
          key={run.id}
          type="button"
          onClick={() => onSelect({ slug: run.slug, id: run.id })}
          className={`flex w-full items-center gap-2 border-b border-gray-100 px-3 py-2 text-left hover:bg-gray-50 ${
            run.id === selectedRunId ? 'bg-blue-50' : ''
          }`}
        >
          <Badge color={RUN_STATUS_COLORS[run.status]} variant="light" className="shrink-0">
            {run.status}
          </Badge>
          <span className="shrink-0 text-sm text-gray-500">#{run.id}</span>
          {showSlug && <span className="truncate font-mono text-xs text-gray-600">{run.slug}</span>}
          {/* Fixed-width columns so dates and relative text align across rows. */}
          <span className="ml-auto w-44 shrink-0 text-right text-xs text-gray-500 tabular-nums">
            {formatDateTime(run.runAt)}
          </span>
          <span className="w-32 shrink-0 text-xs text-gray-400">· {formatRelative(run.runAt)}</span>
          <span className="w-16 shrink-0 text-right text-xs text-gray-500">
            {runDuration(run) ?? ''}
          </span>
        </button>
      ))}
    </div>
  )
}
